import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SeoOptions {
  title: string;
  description?: string;
  /**
   * Optional absolute image URL for Open Graph / Twitter previews. If
   * omitted, social platforms fall back to the static `og:image`
   * declared in `src/index.html` (the brand logo).
   */
  ogImage?: string;
  /**
   * Open Graph object type. Defaults to "website"; insight articles
   * should pass "article" so platforms render the right card.
   */
  ogType?: "website" | "article";
  /**
   * Optional canonical path override (e.g. a property's slug URL). When
   * omitted the canonical + og:url track the current pathname + search.
   * Pass a path beginning with `/`; the origin is added automatically.
   */
  canonicalPath?: string;
}

const META_TAGS: Array<{ selector: string; attr: "name" | "property" }> = [
  { selector: 'meta[name="description"]', attr: "name" },
  { selector: 'meta[property="og:title"]', attr: "property" },
  { selector: 'meta[property="og:description"]', attr: "property" },
  { selector: 'meta[property="og:url"]', attr: "property" },
  { selector: 'meta[property="og:image"]', attr: "property" },
  { selector: 'meta[property="og:type"]', attr: "property" },
  { selector: 'meta[name="twitter:card"]', attr: "name" },
  { selector: 'meta[name="twitter:title"]', attr: "name" },
  { selector: 'meta[name="twitter:description"]', attr: "name" },
  { selector: 'meta[name="twitter:image"]', attr: "name" },
];

function upsertMeta(
  attr: "name" | "property",
  attrValue: string,
  content: string,
): HTMLMetaElement {
  const selector = `meta[${attr}="${attrValue}"]`;
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.content = content;
  return el;
}

function upsertCanonical(href: string): HTMLLinkElement {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

/**
 * Lightweight per-page SEO helper. Updates the document title, meta
 * description, canonical link, and the full Open Graph / Twitter card
 * stack on mount. On unmount the previous values are restored so a
 * page navigation never leaves stale meta behind.
 *
 * Routing is client-side, so search engines that don't execute JS will
 * only see the static <title> / og tags declared in `src/index.html`.
 * Google and the major social crawlers (Facebook, Twitter, LinkedIn,
 * Slack) all run JS and pick up the resolved values, so per-page meta
 * is genuinely useful for link previews and search snippets.
 */
export function useSeo({
  title,
  description,
  ogImage,
  ogType,
  canonicalPath,
}: SeoOptions) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    /* Snapshot every meta tag we plan to mutate so we can faithfully
     * restore them on unmount — otherwise a back-navigation from an
     * article would leave the article's image in the OG card. */
    const previous = new Map<string, string | null>();
    for (const { selector } of META_TAGS) {
      const el = document.querySelector<HTMLMetaElement>(selector);
      previous.set(selector, el?.content ?? null);
    }
    const previousCanonical =
      document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href ??
      null;

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("name", "twitter:description", description);
      upsertMeta("property", "og:description", description);
    }
    upsertMeta("property", "og:title", title);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("property", "og:type", ogType ?? "website");

    /* Canonical + og:url track the current path. We deliberately
     * include the search query for paginated catalogue URLs but drop
     * the hash, since fragment identifiers aren't part of the
     * canonical address. */
    if (typeof window !== "undefined") {
      const canonical = canonicalPath
        ? `${window.location.origin}${canonicalPath}`
        : `${window.location.origin}${pathname}${search}`;
      upsertMeta("property", "og:url", canonical);
      upsertCanonical(canonical);
    }

    if (ogImage) {
      // Root-relative covers (local static assets) must be absolute for
      // social crawlers.
      const resolved =
        ogImage.startsWith("/") && typeof window !== "undefined"
          ? `${window.location.origin}${ogImage}`
          : ogImage;
      upsertMeta("property", "og:image", resolved);
      upsertMeta("name", "twitter:image", resolved);
      // Large preview card when we have a real image to show.
      upsertMeta("name", "twitter:card", "summary_large_image");
    } else if (typeof window !== "undefined") {
      // No page-specific image — default to the final brand logo, made
      // absolute against the current origin so it resolves on whatever
      // domain we're deployed to (no hardcoded host).
      const brandImage = `${window.location.origin}/og-image.png`;
      upsertMeta("property", "og:image", brandImage);
      upsertMeta("name", "twitter:image", brandImage);
      // Basic summary card so the logo isn't rendered full-bleed.
      upsertMeta("name", "twitter:card", "summary");
    } else {
      upsertMeta("name", "twitter:card", "summary");
    }

    return () => {
      document.title = previousTitle;
      for (const { selector, attr } of META_TAGS) {
        const value = previous.get(selector);
        const el = document.querySelector<HTMLMetaElement>(selector);
        if (el && value !== null && value !== undefined) {
          el.content = value;
        } else if (el && value === null) {
          // Tag didn't exist before this page mounted — remove the one
          // we created so the global state stays clean.
          // Only remove tags that have the same identifying attribute
          // we used; matching by selector ensures we don't strip
          // anything we didn't add.
          const stillBlank = !el.content;
          if (stillBlank) {
            el.remove();
          }
        }
      }
      if (previousCanonical !== null) {
        upsertCanonical(previousCanonical);
      } else {
        document.querySelector('link[rel="canonical"]')?.remove();
      }
    };
  }, [title, description, ogImage, ogType, canonicalPath, pathname, search]);
}
